import React, { useCallback, useMemo, useState, useSyncExternalStore } from 'react';
import { getAllSegments } from '@goldcast/player-config/src/utils/clipUtils';
import { SegmentType } from '@goldcast/player-config/src/types/Segment/Type';
import { VideoSegment } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Segment/VideoSegment';
import PlayerTimelineButton from './PlayerTimelineButton';
import { getPlayerTimeFromSegmentTime } from '../timeUtils';
import useCreateClip from '../../hooks/useCreateClip';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import useEditorRestrictions from '@/hooks/useEditorRestrictions';
import { transcriptTimelineSelectionStore } from '@/stores/transcriptTimelineSelection';
import { CLIP_MIN_DURATION } from '@/components/molecules/Transcript/constants';

export default function CreateClipSelected() {
  const { clipData } = useClipsContext();
  const { isBlocked } = useEditorRestrictions();
  const { onCreateClip } = useCreateClip({ clip: clipData });
  const [isCreatingClip, setIsCreatingClip] = useState(false);
  const { startTime, endTime, isSelecting, startSegmentId, endSegmentId } = useSyncExternalStore(
    transcriptTimelineSelectionStore.subscribe,
    transcriptTimelineSelectionStore.getSnapshot
  );

  const shouldShowCreateClipButton = useMemo(() => {
    if (isSelecting) {
      return false;
    }
    if (startTime === null || endTime === null || startSegmentId === null || endSegmentId === null) {
      return false;
    }
    const allSegments = getAllSegments(clipData);

    const startIndex = allSegments.findIndex(s => s.id === startSegmentId);
    const endIndex = allSegments.findIndex(s => s.id === endSegmentId);

    const selectedSegments = allSegments.slice(startIndex, endIndex + 1);

    const hasCompositeSegment = selectedSegments.some(segment => segment.type === SegmentType.COMPOSITE);

    if (!hasCompositeSegment) {
      return false;
    }
    const startSegment = allSegments[startIndex];
    if (!startSegment) {
      return false;
    }
    const endSegment = allSegments[endIndex];
    if (!endSegment) {
      return false;
    }

    if (startSegmentId === 'intro' || startSegmentId === 'outro') {
      return startTime === 0;
    }

    if (endSegmentId === 'intro' || endSegmentId === 'outro') {
      return endTime === (endSegment as VideoSegment).segment_metadata?.maxVideoDuration;
    }

    return true;
  }, [isSelecting, startTime, endTime, startSegmentId, endSegmentId, clipData]);

  const clickHandler = useCallback(async () => {
    try {
      setIsCreatingClip(true);
      await onCreateClip(startTime, endTime, startSegmentId || undefined, endSegmentId || undefined);
    } finally {
      setIsCreatingClip(false);
    }
  }, [endSegmentId, endTime, onCreateClip, startSegmentId, startTime]);

  if (
    !shouldShowCreateClipButton ||
    startTime === null ||
    endTime === null ||
    startSegmentId === null ||
    endSegmentId === null
  ) {
    return <></>;
  }
  const clipDuration =
    getPlayerTimeFromSegmentTime(endTime, clipData, endSegmentId) -
    getPlayerTimeFromSegmentTime(startTime, clipData, startSegmentId);

  let tooltipContent = 'Create Clip';
  if (isCreatingClip) {
    tooltipContent = 'Creating clip...';
  }
  if (clipDuration < CLIP_MIN_DURATION) {
    tooltipContent = `Clip duration must be at least ${CLIP_MIN_DURATION} seconds`;
  }

  const isDisabled = isCreatingClip || clipDuration < CLIP_MIN_DURATION || isBlocked('clip-create');
  return (
    <PlayerTimelineButton
      tooltipTitle={tooltipContent}
      buttonId="create-clip-button"
      onClick={clickHandler}
      isDisabled={isDisabled}
      isLoading={isCreatingClip}
      iconName="IconMovie"
    />
  );
}
